import React, { useCallback, useEffect, useState } from 'react'

//import { tablePendingCreMock } from "./components/TablePendingCre/mock"
import { Card } from 'components'
import { useLoading, useModal, usePersistForm, useToggle } from 'hooks'
import moment from 'moment'
import {
  IPendingCreSearch,
  ISearchFilterPendingParams,
  PendingCreFilterServices,
  PendingCreServices
} from 'services/NGT/PendingCre'
import { SPACING } from 'utils'

import { ViewContractModal } from '../ViewContractModal/ViewContractModal'
import { FilterPendingCre, TablePendingCre } from './components'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

export interface IPendingCreSearchForm {
  CdContrato: string
  NmFornecedor: string
  CompetenciaInicio: string
  CompetenciaFim: string
  CdCRE: string
  NmAnalista: string
  DtAnaliseInicio: string
  DtAnaliseFim: string
  DtAnexoInicio: string
  DtAnexoFim: string
  DtPeriodoInicio: string
  DtPeriodoFim: string
  FlBacklog: boolean
  FlCreBloqueado: boolean
  FlSubContrato: boolean
  FlTodos: boolean
  firstGet?: boolean
}

const SCHEMA = yup.object({
  CdContrato: yup.string().notRequired().nullable(),
  NmFornecedor: yup.string().notRequired().nullable(),
  CompetenciaInicio: yup
    .date()
    .notRequired()
    .typeError('Digite uma data válida.')
    .nullable(),
  CompetenciaFim: yup
    .date()
    .notRequired()
    .nullable()
    .typeError('Digite uma data válida.')
    .min(
      yup.ref('CompetenciaInicio'),
      'Competência final deve ser maior que competência inicio'
    ),
  CdCRE: yup.string().notRequired().nullable(),
  NmAnalista: yup.string().notRequired().nullable(),
  DtAnaliseInicio: yup
    .date()
    .notRequired()
    .typeError('Digite uma data válida.')
    .nullable(),
  DtAnaliseFim: yup
    .date()
    .typeError('Digite uma data válida.')
    .min(
      yup.ref('DtAnaliseInicio'),
      'Data de análise final deve ser maior que a data análise inicio.'
    )
    .nullable(),
  DtAnexoInicio: yup
    .date()
    .notRequired()
    .nullable()
    .typeError('Digite uma data válida.'),
  DtAnexoFim: yup
    .date()
    .min(
      yup.ref('DtAnexoInicio'),
      'Data de anexo final deve ser maior que a data anexo inicio.'
    )
    .typeError('Digite uma data válida.')
    .nullable(),
  DtPeriodoInicio: yup
    .date()
    .notRequired()
    .nullable()
    .typeError('Digite uma data válida.'),
  DtPeriodoFim: yup
    .date()
    .min(
      yup.ref('DtPeriodoInicio'),
      'Data de periodo final deve ser maior que a data periodo inicio.'
    )
    .typeError('Digite uma data válida.')
    .nullable(),
  FlBacklog: yup.bool().notRequired().nullable(),
  FlCreBloqueado: yup.bool().notRequired().nullable(),
  FlSubContrato: yup.bool().notRequired().nullable(),
  FlTodos: yup.bool().notRequired().nullable()
})

const PendingCreSearch = () => {
  const { setLoading } = useLoading()
  const { showModal } = useModal()
  const [openViewContractModal, toggleViewContractModal] = useToggle()
  const [selectedContractId, setSelectedContractId] = useState(0)
  const [pendingCreTableData, setPendingCreTableData] = useState<
    IPendingCreSearch[]
  >([])
  const [page, setPage] = useState<number>(1)
  const [rowsPerPage, setRowsPerPage] = useState<number>(15)
  const [totalItems, setTotalItems] = useState<number>(0)
  const [searchState, setSearchState] = useState<boolean>(false) // constrols use effect change on clear input

  const {
    control,
    watch,
    setValue,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<IPendingCreSearchForm>({
    resolver: yupResolver(SCHEMA),
    defaultValues: {
      CompetenciaInicio: null,
      CompetenciaFim: null,
      DtAnaliseInicio: null,
      DtAnaliseFim: null,
      DtAnexoInicio: null,
      DtAnexoFim: null,
      DtPeriodoInicio: null,
      DtPeriodoFim: null,
      FlCreBloqueado: false,
      FlBacklog: false,
      FlSubContrato: false,
      FlTodos: true,
      firstGet: true
    }
  })

  const CompetenciaInicio = watch('CompetenciaInicio')
  const CompetenciaFim = watch('CompetenciaFim')
  const DtAnaliseInicio = watch('DtAnaliseInicio')
  const DtAnaliseFim = watch('DtAnaliseFim')
  const DtAnexoInicio = watch('DtAnexoInicio')
  const DtAnexoFim = watch('DtAnexoFim')
  const DtPeriodoInicio = watch('DtPeriodoInicio')
  const DtPeriodoFim = watch('DtPeriodoFim')
  const FlBacklog = watch('FlBacklog')
  const FlCreBloqueado = watch('FlCreBloqueado')
  const FlSubContrato = watch('FlSubContrato')

  usePersistForm('ngt_pending_cre_search', {
    setValue,
    watch
  })

  useEffect(() => {
    if (FlBacklog || FlCreBloqueado || FlSubContrato) {
      setValue('FlTodos', false)
    }
  }, [FlBacklog, FlCreBloqueado, FlSubContrato])

  const handleViewContractModal = (contractId: number) => {
    setSelectedContractId(contractId)
    toggleViewContractModal()
  }

  const areDatesValid = useCallback(() => {
    const validDates =
      moment(CompetenciaInicio || moment()).isValid() &&
      moment(CompetenciaFim || moment()).isValid() &&
      moment(DtAnaliseInicio || moment()).isValid() &&
      moment(DtAnaliseFim || moment()).isValid() &&
      moment(DtAnexoInicio || moment()).isValid() &&
      moment(DtAnexoFim || moment()).isValid() &&
      moment(DtPeriodoInicio || moment()).isValid() &&
      moment(DtPeriodoFim || moment()).isValid()

    const isCompetenciaValid =
      CompetenciaInicio && CompetenciaFim
        ? moment(CompetenciaInicio).isSameOrBefore(CompetenciaFim)
        : true

    const isAnaliseValid =
      DtAnaliseInicio && DtAnaliseFim
        ? moment(DtAnaliseInicio).isSameOrBefore(DtAnaliseFim)
        : true

    const isAnexoValid =
      DtAnexoInicio && DtAnexoFim
        ? moment(DtAnexoInicio).isSameOrBefore(DtAnexoFim)
        : true

    const isPeriodoValid =
      DtPeriodoInicio && DtPeriodoFim
        ? moment(DtPeriodoInicio).isSameOrBefore(DtPeriodoFim)
        : true

    return (
      validDates &&
      isCompetenciaValid &&
      isAnaliseValid &&
      isAnexoValid &&
      isPeriodoValid
    )
  }, [
    CompetenciaInicio,
    CompetenciaFim,
    DtAnaliseInicio,
    DtAnaliseFim,
    DtAnexoInicio,
    DtAnexoFim,
    DtPeriodoInicio,
    DtPeriodoFim
  ])

  const handleOnchange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // setForm((prevState) => ({
    //   ...prevState,
    //   [event.target.name]: event.target.value
    // }))
  }

  const handleClearButton = () => {
    reset()
    handleCLickButtonSearch()
    handleSearchPendingCre()
  }

  const handleCLickButtonSearch = () => {
    if (page === 1) {
      setSearchState((oldValue) => !oldValue)
      return
    }

    setPage(1)
  }

  const handleSearchPendingCre = (
    isClick?: boolean,
    form: IPendingCreSearchForm = watch()
  ) => {
    setLoading('loading')

    if (
      !form.FlTodos &&
      !form.FlBacklog &&
      !form.FlCreBloqueado &&
      !form.FlSubContrato
    ) {
      form.FlTodos = true
      setValue('FlTodos', true)
    }

    const requestData: ISearchFilterPendingParams = {
      CdContrato: form.CdContrato ?? null,
      CdCRE: form.CdCRE ?? null,
      NmAnalista: form.NmAnalista ?? null,
      NmFornecedor: form.NmFornecedor ?? null,
      ...(form.FlBacklog && {
        FlBacklog: form.FlBacklog
      }),
      ...(form.FlCreBloqueado && {
        FlCreBloqueado: form.FlCreBloqueado
      }),
      ...(form.FlSubContrato && {
        FlSubContrato: form.FlSubContrato
      }),
      ...(form.FlTodos && {
        FlTodos: form.FlTodos
      }),
      ...(form.CompetenciaFim && {
        CompetenciaFim:
          parseInt(moment(form.CompetenciaFim).format('YYYYMM')) || null
      }),
      ...(form.CompetenciaInicio && {
        CompetenciaInicio:
          parseInt(moment(form.CompetenciaInicio).format('YYYYMM')) || null
      }),
      ...(form.DtAnaliseFim && {
        DtAnaliseFim: moment(form.DtAnaliseFim).toISOString() || null
      }),
      ...(form.DtAnaliseInicio && {
        DtAnaliseInicio: moment(form.DtAnaliseInicio).toISOString() || null
      }),
      ...(form.DtAnexoFim && {
        DtAnexoFim: moment(form.DtAnexoFim).toISOString() || null
      }),
      ...(form.DtAnexoInicio && {
        DtAnexoInicio: moment(form.DtAnexoInicio).toISOString() || null
      }),
      ...(form.DtPeriodoFim && {
        DtPeriodoFim: moment(form.DtPeriodoFim).toISOString() || null
      }),
      ...(form.DtPeriodoInicio && {
        DtPeriodoInicio: moment(form.DtPeriodoInicio).toISOString() || null
      }),
      PageNumber: isClick ? 1 : page,
      PageSize: rowsPerPage
    }

    try {
      PendingCreFilterServices()
        .getPesquisaPendencias(requestData)
        .then((response) => {
          if (response.status !== 200) throw new Error(response.statusText)
          setPendingCreTableData(response.data.items)
          setTotalItems(response.data.pageCount)
          setLoading('idle')
          setValue('firstGet', false)
        })
    } catch (error: any) {
      setLoading('idle')
      showModal({
        hideButtonCancel: true,
        message: 'Não foi possível obter os resultados da pesquisa',
        txtButtonConfirm: 'OK',
        type: 'error'
      })
    }
  }

  useEffect(() => {
    if (!watch('firstGet')) {
      handleSearchPendingCre()
    }
  }, [page, rowsPerPage, searchState])

  const [dateErrors, setDateErrors] = useState<any>(null)

  return (
    <>
      <Card
        marginLeft={SPACING.spacing20}
        marginRight={SPACING.spacing20}
        marginBottom={SPACING.spacing100}
        padding={SPACING.spacing20}
        boxShadow={`0px -1px 32px rgba(50, 50, 71, 0.12), 0px 24px 32px rgba(50, 50, 71, 0.12)`}
        borderRadius={`0px 10px 0px 0px`}
      >
        <FilterPendingCre
          control={control}
          handleSubmit={handleSubmit}
          errors={errors}
          onChange={handleOnchange}
          handleClearButton={handleClearButton}
          handleSearchPendingCre={handleSearchPendingCre}
          dateErrors={dateErrors}
          watch={watch}
        />
        <TablePendingCre
          tableData={pendingCreTableData}
          totalItems={totalItems}
          page={page}
          areDatesValid={areDatesValid}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          selectedContractInfo={handleViewContractModal}
          dateErrors={dateErrors}
        />
      </Card>
      <ViewContractModal
        showModal={openViewContractModal}
        toggleModal={toggleViewContractModal}
        contractID={selectedContractId}
      />
    </>
  )
}

export default PendingCreSearch
